import { useState, useEffect } from "react";
import { Users, X, Trash2, Clock, Bell, Shield, UserMinus, Loader2, RefreshCw, AlertTriangle, CheckCircle2, FileText } from "lucide-react";
import api from "@/lib/api";
import { useAppContext } from "@/App";
import ReportSettings from "./ReportSettings";

export default function HubAdmin({ hubId, onClose }) {
  const { auth, session } = useAppContext();
  const [members, setMembers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("members");

  const load = async () => {
    setLoading(true);
    try {
      const [m, n] = await Promise.all([
        api.getHubMembers(hubId),
        api.getAdminNotifications(hubId),
      ]);
      setMembers(m);
      setNotifications(n);
    } catch (e) { void 0; }
    setLoading(false);
  };

  useEffect(() => { load(); }, [hubId]);

  const handleKick = async (email) => {
    if (!window.confirm(`Remove ${email} from hub?`)) return;
    await api.kickMember({ hub_id: hubId, user_email: email });
    load();
  };

  const handleMarkRead = async (notifId) => {
    await api.markNotifRead(notifId);
    load();
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const activeMembers = members.filter(m => m.status !== "SHADOW");
  const shadowMembers = members.filter(m => m.status === "SHADOW");

  return (
    <div className="fixed inset-0 z-[850] bg-white/95 overflow-y-auto" data-testid="hub-admin">
      <div className="max-w-3xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sticky top-0 bg-white/95 backdrop-blur-sm py-4 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-slate-950" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-900 uppercase tracking-tight">Hub Admin</h1>
              <p className="text-xs text-slate-400 font-bold">{members.length} members | {unreadCount} notifications</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="p-2.5 bg-slate-100 rounded-xl text-slate-400 hover:text-slate-900"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={onClose} className="p-2.5 bg-slate-100 rounded-xl text-slate-400 hover:text-slate-900" data-testid="close-hub-admin"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "members", label: "Members", icon: Users },
            { id: "notifications", label: "Notifications", icon: Bell, badge: unreadCount },
            { id: "reports", label: "Reports", icon: FileText },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase flex items-center gap-2 transition-all ${
                tab === t.id ? "bg-amber-500 text-slate-950" : "bg-slate-50 border border-slate-200 text-slate-400 hover:text-slate-900"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {t.badge > 0 && <span className="w-5 h-5 bg-red-600 text-white text-[9px] font-black rounded-full flex items-center justify-center">{t.badge}</span>}
            </button>
          ))}
        </div>

        {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-lime-500 animate-spin" /></div>}

        {/* Members Tab */}
        {!loading && tab === "members" && (
          <div className="space-y-2">
            {activeMembers.length > 0 && (
              <>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-2">Active Members ({activeMembers.length})</p>
                {activeMembers.map(m => (
                  <MemberRow key={m.email} member={m} onKick={() => handleKick(m.email)} />
                ))}
              </>
            )}
            {shadowMembers.length > 0 && (
              <>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-6 mb-2">Shadow Users ({shadowMembers.length})</p>
                {shadowMembers.map(m => (
                  <MemberRow key={m.email} member={m} onKick={() => handleKick(m.email)} isShadow />
                ))}
              </>
            )}
            {members.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-12">No members yet</p>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {!loading && tab === "notifications" && (
          <div className="space-y-2">
            {notifications.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-12">No notifications</p>
            ) : notifications.map(n => (
              <div key={n.id} className={`p-3 rounded-xl border flex items-center justify-between ${
                n.read ? "border-slate-200 bg-slate-50/80" : "border-red-500/30 bg-red-500/5"
              }`} data-testid={`notif-${n.id}`}>
                <div className="flex items-center gap-3">
                  {!n.read && <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
                  <div>
                    <p className="text-xs font-bold text-slate-800">{n.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                        n.type === "MEMBER_INACTIVE_KICKED" ? "bg-red-500/10 text-red-400" : "bg-slate-100 text-slate-400"
                      }`}>{n.type}</span>
                      <span className="text-[9px] text-slate-400">{new Date(n.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                {!n.read && (
                  <button onClick={() => handleMarkRead(n.id)} className="px-3 py-1.5 bg-slate-100 text-slate-400 text-[10px] font-bold rounded hover:text-slate-900">
                    Mark Read
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

        {/* Reports Tab */}
        {!loading && tab === "reports" && (
          <ReportSettings hubId={hubId} jwt={auth?.jwt} lang={session?.language || localStorage.getItem("pigeon-lang") || "en"} />
        )}
    </div>
  );
}

function MemberRow({ member, onKick, isShadow }) {
  return (
    <div className={`p-3 rounded-xl border flex items-center justify-between ${
      isShadow ? "border-slate-200 bg-slate-50/50 opacity-60" : "border-slate-200 bg-slate-50"
    }`} data-testid={`member-${member.email}`}>
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black ${
          member.role === "planner" ? "bg-lime-500 text-slate-950" :
          member.role === "driver" ? "bg-red-600 text-white" :
          member.role === "machinist" ? "bg-amber-500 text-slate-950" :
          member.role === "service" ? "bg-purple-600 text-white" :
          member.role === "transport" ? "bg-blue-600 text-white" :
          "bg-slate-700 text-slate-400"
        }`}>{member.role?.[0]?.toUpperCase()}</div>
        <div>
          <p className="text-xs font-bold text-slate-800">{member.email}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[9px] font-bold text-slate-400 uppercase">{member.role}</span>
            {member.inactive_days > 0 && (
              <span className={`text-[9px] font-bold flex items-center gap-0.5 ${
                member.inactive_days > 30 ? "text-red-400" : member.inactive_days > 7 ? "text-amber-400" : "text-slate-400"
              }`}>
                <Clock className="w-2.5 h-2.5" /> {member.inactive_days}d ago
              </span>
            )}
            {isShadow && <span className="text-[9px] font-bold bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">SHADOW</span>}
          </div>
        </div>
      </div>
      <button onClick={onKick} className="p-2 text-slate-400 hover:text-red-400 transition-all" data-testid={`kick-${member.email}`}>
        <UserMinus className="w-4 h-4" />
      </button>
    </div>
  );
}
